// src/pages/admin/UserListPage.tsx
// ============================================================
// Summit League v2 — Admin User List Page
//
// Displays a searchable, filterable, sortable, paginated table
// of all users. Clicking a row navigates to the user detail page.
//
// Filters: search (username/email), role, status, game
// Sort: username, created_at, last_login_at, status
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import UserStatusBadge from '../../components/admin/UserStatusBadge';
import UserRoleBadge from '../../components/admin/UserRoleBadge';

import '../../styles/admin-users.css';

interface UserRow {
  user_id: number;
  username: string;
  email: string;
  role: string;
  avatar_url: string | null;
  status: string;
  ban_reason: string | null;
  banned_by_username: string | null;
  last_login_at: string | null;
  created_at: string;
  team_count: number;
}

interface PaginationData {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

type SortField = 'username' | 'created_at' | 'last_login_at' | 'status';

export default function UserListPage() {
  const navigate = useNavigate();

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Data
  const [users, setUsers] = useState<UserRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on search change
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params: Record<string, string | number> = {
        page,
        limit: 25,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await axiosClient.get('/admin/users', { params });
      setUsers(res.data.data.users);
      setPagination(res.data.data.pagination);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder, debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const sortIcon = (field: SortField) => {
    if (sortBy !== field) return ' ↕';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="sl-admin-users">
      <div className="sl-admin-page-header">
        <h1 className="sl-admin-page-title">Users</h1>
        {pagination && (
          <span className="sl-admin-page-count">{pagination.totalCount} total</span>
        )}
      </div>

      {/* Filters Bar */}
      <div className="sl-user-filters">
        <div className="sl-user-filter-search">
          <input
            type="text"
            className="sl-user-search-input"
            placeholder="Search by username or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="sl-user-filter-selects">
          <select
            className="sl-user-filter-select"
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="moderator">Moderator</option>
            <option value="admin">Admin</option>
          </select>

          <select
            className="sl-user-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && <div className="sl-admin-error">{error}</div>}

      {/* Table */}
      <div className="sl-user-table-wrapper">
        <table className="sl-user-table">
          <thead>
            <tr>
              <th className="sl-user-th sl-user-th--sortable" onClick={() => handleSort('username')}>
                User{sortIcon('username')}
              </th>
              <th className="sl-user-th">Role</th>
              <th className="sl-user-th sl-user-th--sortable" onClick={() => handleSort('status')}>
                Status{sortIcon('status')}
              </th>
              <th className="sl-user-th">Teams</th>
              <th className="sl-user-th sl-user-th--sortable" onClick={() => handleSort('last_login_at')}>
                Last Login{sortIcon('last_login_at')}
              </th>
              <th className="sl-user-th sl-user-th--sortable" onClick={() => handleSort('created_at')}>
                Joined{sortIcon('created_at')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr>
                <td colSpan={6} className="sl-user-td sl-user-td--loading">
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="sl-user-td sl-user-td--empty">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.user_id}
                  className="sl-user-row"
                  onClick={() => navigate(`/admin/users/${user.user_id}`)}
                >
                  <td className="sl-user-td">
                    <div className="sl-user-identity">
                      <div className="sl-user-avatar-sm">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="sl-user-avatar-img" />
                        ) : (
                          <span className="sl-user-avatar-fallback">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="sl-user-name-group">
                        <span className="sl-user-username">{user.username}</span>
                        <span className="sl-user-email">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="sl-user-td">
                    <UserRoleBadge role={user.role} />
                  </td>
                  <td className="sl-user-td">
                    <UserStatusBadge status={user.status} />
                  </td>
                  <td className="sl-user-td sl-user-td--center">{user.team_count}</td>
                  <td className="sl-user-td sl-user-td--date">{formatDate(user.last_login_at)}</td>
                  <td className="sl-user-td sl-user-td--date">{formatDate(user.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="sl-user-pagination">
          <button
            className="sl-user-pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            ← Prev
          </button>
          <span className="sl-user-pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="sl-user-pagination-btn"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
